// VeriLink ML client — calls the backend scikit-learn endpoints and merges the
// model score into the existing heuristic result. The heuristic still powers the
// rich flag/detail UI and provides the hard-red-flag protection + offline
// fallback if the backend is unreachable.
import { verifyLink, verifyEmail, verifyArticle } from './verificationLogic';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const post = async (path, body) => {
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return { ok: false };
    const data = await res.json();
    return { ok: true, analysis: data.analysis };
  } catch (e) {
    return { ok: false };
  }
};

const linkVerdict = (score, hasHardFlag) =>
  hasHardFlag || score < 50 ? 'Dangerous' : score < 80 ? 'Caution' : 'Safe';

export const runLinkAnalysis = async (url) => {
  const local = verifyLink(url);
  const ml = await post('/api/ml/link', { url });
  if (ml.ok && local.verdict !== 'Invalid') {
    let score = ml.analysis.safetyScore;
    const hasHardFlag = (local.hardRedFlags || []).length > 0;
    if (hasHardFlag) score = Math.min(score, 40); // preserve protection
    local.safetyScore = score;
    local.verdict = linkVerdict(score, hasHardFlag);
    local.ml = { ...ml.analysis, available: true };
  } else {
    local.ml = { available: false, source: 'heuristic-local' };
  }
  return local;
};

export const runEmailAnalysis = async (email, content = '') => {
  const local = verifyEmail(email, content);
  const ml = await post('/api/ml/email', { email, content });
  if (ml.ok) {
    // ML-primary: the backend already applies reliable hard-caps (sender
    // impersonation / dangerous links) and owns body phishing intent, so trust
    // its risk + verdict rather than the over-eager client keyword heuristic.
    local.overallRiskScore = ml.analysis.riskScore;
    local.verdict = ml.analysis.verdict;
    local.criticalSignals = ml.analysis.criticalSignals || [];
    local.hardRedFlags = ml.analysis.criticalSignals || [];
    local.ml = { ...ml.analysis, available: true };
    // Keep the detailed breakdown consistent: if the model deems it safe, don't
    // surface client-only "critical" flags (e.g. a benign "reset your password").
    if (ml.analysis.riskScore < 30) {
      if (local.contentAnalysis) local.contentAnalysis.hardRuleTriggered = null;
      local.topReasons = (local.topReasons || []).filter((f) => f.severity !== 'critical');
    }
  } else {
    local.ml = { available: false, source: 'heuristic-local' };
  }
  return local;
};

export const runArticleAnalysis = async (text, sourceUrl = '') => {
  const local = verifyArticle(text, sourceUrl);
  const ml = await post('/api/ml/article', { text, sourceUrl });
  if (ml.ok && local.category !== 'Unknown') {
    let cred = ml.analysis.credibilityScore;
    const n = (local.hardFailureSignals || []).length;
    if (n >= 3) cred = Math.min(cred, 25);          // preserve protection
    else if (n >= 2) cred = Math.min(cred, 40);
    local.credibilityScore = cred;
    local.category = cred >= 80 ? 'High Credibility'
      : cred >= 50 ? 'Mixed / Uncertain' : 'Low Credibility';
    local.ml = { ...ml.analysis, available: true };
  } else {
    local.ml = { available: false, source: 'heuristic-local' };
  }
  return local;
};

// Raw endpoints for the Phishing Lab explainability tab.
export const explainLink = (url) => post('/api/ml/link', { url });
export const explainEmail = (email, content) => post('/api/ml/email', { email, content });
export const explainArticle = (text, sourceUrl) => post('/api/ml/article', { text, sourceUrl });
